import type { DefaultContext } from '@apollo/client';

import {
  initializeApollo,
  initializeSecureApolloClient,
} from '@/graphql/client';
import type {
  CollectionCardsQuery,
  CollectionCardsQueryVariables,
  LanguageCodeEnum,
  OrderByTransactionQuery,
  OrderByTransactionQueryVariables,
  OrderQuery,
  OrderQueryVariables,
  ProductCardsAndPricesQuery,
  ProductCardsAndPricesQueryVariables,
  ProductCardsQuery,
  ProductCardsQueryVariables,
  ProductVariantsBySkusQuery,
  ProductVariantsBySkusQueryVariables,
} from '@/graphql/saleor/api';
import {
  CollectionCardsDocument,
  OrderByTransactionDocument,
  OrderDocument,
  ProductCardsAndPricesDocument,
  ProductCardsDocument,
  ProductVariantsBySkusDocument,
} from '@/graphql/saleor/api';
import type { CollectionData, ProductData } from '@/integrations/contentful';
import {
  NEXT_PUBLIC_IMAGES_FORMAT,
  SSR_MODE,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { retrievePaymentData } from '@/lib/payment/api';

import { ProductsDetailsDocument } from './documents/ProductsDetailsDocument';
import type { ProductsDetailsQuery } from './types/ProductsDetailsQuery';
import type { ProductsDetailsQueryVariables } from './types/ProductsDetailsQueryVariables';

export const fetchProductData = async <T = ProductData[]>(
  variables: ProductCardsAndPricesQueryVariables,
  context?: DefaultContext,
): Promise<T> => {
  // note: this function should only use for fetch cms productCards data in the ssr,
  // however EmptyShoppingBagMobile.tsx call fetchFixedPageData in client.
  if (SSR_MODE) {
    const client = initializeSecureApolloClient();
    const { data } = await client.query<
      ProductCardsAndPricesQuery,
      ProductCardsAndPricesQueryVariables
    >({
      query: ProductCardsAndPricesDocument,
      variables,
      context: context,
    });

    const productCards = mapEdgesToItems(data.products);
    const prices = mapEdgesToItems(data.prices);

    const results = productCards.map((productCard) => {
      const price = prices.find((price) => productCard.slug === price.slug);
      if (price) {
        return {
          ...productCard,
          defaultVariant: {
            ...productCard.defaultVariant,
            pricing: price.defaultVariant?.pricing ?? null,
          },
        };
      }
      return productCard;
    });

    return results as T;
  } else {
    const client = initializeApollo();
    const { data } = await client.query<
      ProductCardsQuery,
      ProductCardsQueryVariables
    >({
      query: ProductCardsDocument,
      variables,
      context: context,
    });

    return mapEdgesToItems(data.products) as T;
  }
};

export const fetchCollectionData = async <T = CollectionData[]>(
  variables: CollectionCardsQueryVariables,
  context?: DefaultContext,
): Promise<T> => {
  const client = initializeApollo();
  const { data } = await client.query<
    CollectionCardsQuery,
    CollectionCardsQueryVariables
  >({
    query: CollectionCardsDocument,
    variables,
    context: context,
  });

  return mapEdgesToItems(data.collections) as T;
};

export const fetchProductsById = async <T = ProductsDetailsQuery>(
  variables: ProductsDetailsQueryVariables,
  context?: DefaultContext,
): Promise<T> => {
  const client = initializeApollo();

  const { data } = await client.query<
    ProductsDetailsQuery,
    ProductsDetailsQueryVariables
  >({
    query: ProductsDetailsDocument,
    variables,
    context: context,
  });

  return data as T;
};

export const fetchProductCardsData = async (
  variables: ProductCardsQueryVariables,
  context?: DefaultContext,
) => {
  const client = initializeApollo();
  const { data, errors } = await client.query<
    ProductCardsQuery,
    ProductCardsQueryVariables
  >({
    query: ProductCardsDocument,
    variables,
    context: context,
  });

  return {
    data,
    errors,
  };
};

export const fetchTransactionByID = async (
  id: string,
  languageCode: LanguageCodeEnum,
  context?: DefaultContext,
) => {
  const client = initializeApollo(null, true);

  const { data } = await client.query<
    OrderByTransactionQuery,
    OrderByTransactionQueryVariables
  >({
    query: OrderByTransactionDocument,
    variables: {
      id,
    },
    context: context,
  });

  const orderData = data.transaction?.order;

  if (orderData) {
    const [{ data }, paymentData] = await Promise.all([
      client.query<OrderQuery, OrderQueryVariables>({
        query: OrderDocument,
        variables: {
          id: orderData.id,
          languageCode,
          thumbnailSize: THUMBNAIL_IMAGE_SIZE,
          imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        },
      }),
      retrievePaymentData({ orderId: orderData.id }),
    ]);
    const order = data?.order;

    if (order) {
      return {
        order,
        orderExtraData: {
          id: orderData.id,
          businessId: orderData.businessId as string,
          expirationTimestamp: 0,
        },
        paymentData,
      };
    }
  }

  return undefined;
};

export const fetchProductVariantBySkus = async (
  variables: ProductVariantsBySkusQueryVariables,
  context?: DefaultContext,
) => {
  const client = initializeApollo();
  const { data } = await client.query<
    ProductVariantsBySkusQuery,
    ProductVariantsBySkusQueryVariables
  >({
    query: ProductVariantsBySkusDocument,
    variables,
    context,
  });

  return { data };
};
